/**
 * Icebox Modal - Freeze a contact for later
 */
import React, { useState } from "react";
import { Snowflake, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

// Quick date presets
const DATE_PRESETS = [
  { label: "1 Week", days: 7 },
  { label: "2 Weeks", days: 14 },
  { label: "1 Month", days: 30 },
  { label: "3 Months", days: 90 },
  { label: "6 Months", days: 180 },
];

const IceboxModal = ({ isOpen, contact, onClose, onSubmit }) => {
  const [wakeDate, setWakeDate] = useState("");
  const [notes, setNotes] = useState("");

  const handlePreset = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    setWakeDate(date.toISOString().split('T')[0]);
  };

  const handleSubmit = () => {
    if (!wakeDate) return;
    onSubmit(wakeDate, notes);
    setWakeDate("");
    setNotes("");
  };

  const handleClose = () => {
    setWakeDate("");
    setNotes("");
    onClose();
  };

  const fullName = contact ? [contact.first_name, contact.last_name].filter(Boolean).join(" ") : "";

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-cyan-400">
            <Snowflake className="h-5 w-5" />
            Move to Icebox
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <p className="text-slate-400 text-sm mb-4">
            Freeze <span className="text-white font-medium">{fullName}</span> until a specific date.
            They'll automatically return to Follow Up on the wake date.
          </p>

          {/* Quick Presets */}
          <div className="mb-4">
            <Label className="text-slate-400 text-xs mb-2 block">Quick Select</Label>
            <div className="flex flex-wrap gap-2">
              {DATE_PRESETS.map(preset => (
                <Button
                  key={preset.days}
                  variant="outline"
                  size="sm"
                  onClick={() => handlePreset(preset.days)}
                  className="border-slate-700 hover:border-cyan-600 hover:text-cyan-400"
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Date Picker */}
          <div className="mb-4">
            <Label className="text-slate-400">Wake Up Date</Label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                type="date"
                value={wakeDate}
                onChange={(e) => setWakeDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="bg-slate-800 border-slate-700 pl-10"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-slate-400">Reason / Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why are we freezing this contact?"
              className="bg-slate-800 border-slate-700 mt-1"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className="border-slate-700">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!wakeDate}
            className="bg-cyan-600 hover:bg-cyan-500"
          >
            <Snowflake className="h-4 w-4 mr-2" />
            Freeze Contact
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IceboxModal;
